"use client"

import { useContext, useEffect, useState, useSyncExternalStore } from "react"

import { QueryClientContext } from "@tanstack/react-query"

import { redactAuthUrl } from "@/platform/api/redact-auth-url"
import { negotiateBrowseHost } from "@/platform/app-mode/browse-history"
import { beginHostedDocument, setAppLocked, setScreenPresentation, setScreenVisible } from "@/platform/app-mode/screen-visibility"
import { resetBridgeEvents } from "@/platform/bridge/document-events"
import { type OutboundData, createErrorEnvelope, createNotification, createRequest, isResultType, parseInboundEnvelope } from "@/platform/bridge/envelope"
import { createHostedSession } from "@/platform/bridge/hosted-session"
import {
  BRIDGE_MESSAGE_DIRECTIONS,
  BRIDGE_PROTOCOL_VERSION,
  type BridgeEnvelope,
  type BridgeErrorCode,
  type BridgeMessageType,
  type SessionProbeResultData,
  type ScreenPresentation,
  type SessionRevokedReason,
} from "@/platform/bridge/types"
import { logger } from "@/platform/observability/logger"
import { recordTelemetry } from "@/platform/observability/telemetry"
import { currentViewer } from "@/platform/session/current-viewer"

import type { QueryClient } from "@tanstack/react-query"

export const BRIDGE_EVENT_NAME = "nelo:bridge"

export const BRIDGE_REQUEST_TIMEOUT_MS = 10_000

export const WEB_CAPABILITIES: ReadonlyArray<BridgeMessageType> = [
  "checkout.start",
  "props",
  "visit",
  "visit.allow",
  "visit.hint",
  "page.meta",
  "screen.presentation",
  "browse.state",
  "browse.command",
  "bridge.click",
  "state.invalidate",
  "checkout.completed",
  "idv.start",
  "idv.completed",
  "analytics.track",
  "checkout.entered",
  "rum.session",
  "share.open",
  "share.story",
  "share.channel",
  "impressions.track",
]

interface NativeHost {
  postMessage: (message: string) => void
}

interface BridgeHostDeclaration {
  protocol?: unknown
  capabilities?: unknown
  referralCode?: unknown
}

interface LegacyHostDeclaration {
  capabilities?: unknown
}

interface HostWindow {
  ReactNativeWebView?: NativeHost
  NeloBridgeHost?: BridgeHostDeclaration
  NeloLegacyHost?: LegacyHostDeclaration
}

const hostWindow = (): HostWindow | undefined => (typeof window === "undefined" ? undefined : (window as unknown as HostWindow))

const nativeHost = (): NativeHost | undefined => hostWindow()?.ReactNativeWebView

export const isAppBridgeAvailable = (): boolean => nativeHost() !== undefined

const subscribeNever = () => () => {}
export const useInNativeHost = (): boolean => useSyncExternalStore(subscribeNever, isAppBridgeAvailable, () => false)

// A shipped app hands every postMessage to its buy handler, so an unsolicited envelope reads as a variant-less add-to-cart (ENGM-687).
export const isEnvelopeHostAvailable = (): boolean => nativeHost() !== undefined && hostWindow()?.NeloBridgeHost?.protocol === BRIDGE_PROTOCOL_VERSION

export const hostSupports = (type: BridgeMessageType): boolean => {
  const declared = hostWindow()?.NeloBridgeHost?.capabilities
  return isEnvelopeHostAvailable() && Array.isArray(declared) && declared.includes(type)
}

export const hostReferralCode = (): string | null | undefined => {
  const declaration = hostWindow()?.NeloBridgeHost
  if (!isEnvelopeHostAvailable() || declaration === undefined || !("referralCode" in declaration)) return undefined
  const { referralCode } = declaration
  return typeof referralCode === "string" || referralCode === null ? referralCode : undefined
}

export const legacyHostSupports = (capability: string): boolean => {
  const declared = hostWindow()?.NeloLegacyHost?.capabilities
  return nativeHost() !== undefined && Array.isArray(declared) && declared.includes(capability)
}

export const postRawToNativeHost = (message: unknown): boolean => {
  const host = nativeHost()
  if (!host) return false

  host.postMessage(JSON.stringify(message))
  return true
}

export const postToNativeHost = (envelope: BridgeEnvelope): boolean => (isEnvelopeHostAvailable() ? postRawToNativeHost(envelope) : false)

export class BridgeRequestError extends Error {
  readonly code: BridgeErrorCode

  constructor(code: BridgeErrorCode, message: string) {
    super(message)
    this.name = "BridgeRequestError"
    this.code = code
  }
}

type InboundHandler = (data: Record<string, unknown>, envelope: BridgeEnvelope) => void

export interface NativeBridge {
  readonly available: boolean
  readonly send: <T extends BridgeMessageType>(type: T, data?: OutboundData<T>) => boolean
  readonly request: <T extends BridgeMessageType>(type: T, data?: OutboundData<T>, timeoutMs?: number) => Promise<Record<string, unknown>>
  readonly on: (type: BridgeMessageType, handler: InboundHandler) => () => void
  readonly notify: <T extends BridgeMessageType>(type: T, id: string, data?: OutboundData<T>) => boolean
  readonly dispose: () => void
}

interface PendingRequest {
  resultType: string
  viewer?: string
  resolve: (data: Record<string, unknown>) => void
  reject: (error: BridgeRequestError) => void
  timer: ReturnType<typeof setTimeout>
}

interface BridgeOptions {
  viewer?: () => string | null
  probeSession?: (handoff?: string) => Promise<SessionProbeResultData>
  sessionRevoked?: (reason?: SessionRevokedReason) => void
  dispose?: () => void
}

export const createNativeBridge = (options: BridgeOptions = {}): NativeBridge => {
  let disposed = false
  let pageMeta: BridgeEnvelope | undefined
  const probing = new Set<string>()
  const received = new Set<string>()
  const initialMessages = new Map<BridgeMessageType, BridgeEnvelope>()
  const registered = new Set<BridgeMessageType>()
  const available = isEnvelopeHostAvailable()
  const pending = new Map<string, PendingRequest>()
  const handlers = new Map<BridgeMessageType, Set<InboundHandler>>()

  const settle = (envelope: BridgeEnvelope): void => {
    const id = envelope.id
    if (id === undefined) return

    const waiting = pending.get(id)
    if (!waiting || (envelope.type !== "error" && envelope.type !== waiting.resultType)) return

    pending.delete(id)
    clearTimeout(waiting.timer)

    if (envelope.error !== undefined) {
      const error = new BridgeRequestError(envelope.error.code, envelope.error.message)
      if (error.code === "failed") logger.error("native.request_failed", { viewer: waiting.viewer, request_id: id, message_type: waiting.resultType }, error)
      waiting.reject(error)
      return
    }

    waiting.resolve(envelope.data ?? {})
  }

  const receive = (event: Event) => {
    const parsed = parseInboundEnvelope((event as CustomEvent<unknown>).detail)

    if (!parsed.ok) {
      postToNativeHost(createErrorEnvelope(parsed.error.code, parsed.error.message))
      return
    }

    const envelope = parsed.envelope
    if (envelope.type === "app.locked") setAppLocked(true)
    if (envelope.type === "app.unlocked") setAppLocked(false)
    if (envelope.type === "screen.presentation") {
      setScreenPresentation(envelope.data?.presentation as ScreenPresentation)
      negotiateBrowseHost()
    }
    if (envelope.type === "screen.visibility") setScreenVisible(envelope.data?.visible === true)
    if (envelope.type === "session.probe" && options.probeSession) {
      const id = envelope.id
      if (!id || probing.has(id)) return
      probing.add(id)
      void options.probeSession(typeof envelope.data?.handoff === "string" ? envelope.data.handoff : undefined).then(
        (data) => {
          probing.delete(id)
          if (disposed) return
          postToNativeHost({ v: 1, type: "session.probe.result", id, data })
          if (!disposed && data.status === "authenticated" && pageMeta) postToNativeHost(pageMeta)
        },
        () => {
          probing.delete(id)
          if (!disposed) postToNativeHost({ v: 1, type: "session.probe.result", id, error: { code: "failed", message: "Session lookup unavailable" } })
        }
      )
      return
    }
    if (envelope.type === "session.revoked" && options.sessionRevoked) {
      pageMeta = undefined
      const reason = envelope.data?.reason
      options.sessionRevoked(reason === "identity-mismatch" || reason === "signed-out" ? reason : undefined)
    }

    if (isResultType(parsed.envelope.type) || (parsed.envelope.type === "error" && parsed.envelope.id !== undefined && pending.has(parsed.envelope.id))) {
      settle(parsed.envelope)
      return
    }

    if (options.probeSession && envelope.type === "rum.session" && envelope.id === undefined) return
    if (envelope.id !== undefined) {
      const key = `${envelope.type}:${envelope.id}`
      if (received.has(key)) return
      received.add(key)
    }
    const listeners = handlers.get(parsed.envelope.type)
    if (!listeners) {
      if (["props", "rum.session", "browse.command"].includes(envelope.type) && !registered.has(envelope.type)) {
        initialMessages.set(envelope.type, envelope)
        return
      }
      if (["app.locked", "app.unlocked", "screen.visibility", "screen.presentation"].includes(envelope.type)) return
      if (envelope.type === "session.revoked" && options.sessionRevoked) return
      postToNativeHost(createErrorEnvelope("unknown_type", `no handler for '${parsed.envelope.type}'`))
      return
    }

    for (const handler of listeners) handler(parsed.envelope.data ?? {}, parsed.envelope)
  }

  if (available) {
    beginHostedDocument()
    resetBridgeEvents()
    window.addEventListener(BRIDGE_EVENT_NAME, receive)
    postToNativeHost(
      createNotification("ready", {
        url: redactAuthUrl(window.location.href),
        capabilities: options.probeSession ? [...WEB_CAPABILITIES, "session.probe"] : WEB_CAPABILITIES,
        protocol: BRIDGE_PROTOCOL_VERSION,
      })
    )
  }

  return {
    available,
    send: (type, data) => {
      if (disposed || !available) return false
      const envelope = createNotification(type, data)
      if (type === "page.meta") pageMeta = JSON.parse(JSON.stringify(envelope)) as BridgeEnvelope
      return postToNativeHost(envelope)
    },
    notify: (type, id, data) => !disposed && available && postToNativeHost(createRequest(type, id, data)),
    request: (type, data, timeoutMs = BRIDGE_REQUEST_TIMEOUT_MS) =>
      new Promise((resolve, reject) => {
        let viewer: string | undefined
        recordTelemetry(() => {
          viewer = options.viewer?.() ?? undefined
        })
        if (disposed) {
          reject(new BridgeRequestError("cancelled", "Document disposed"))
          return
        }

        if (BRIDGE_MESSAGE_DIRECTIONS[type] !== "web_to_native") {
          reject(new BridgeRequestError("invalid_data", `'${type}' is ${BRIDGE_MESSAGE_DIRECTIONS[type]}`))
          return
        }

        if (!available) {
          reject(new BridgeRequestError("capability_unavailable", `'${type}' needs the Nelo app`))
          return
        }

        const id = crypto.randomUUID()
        const timer = setTimeout(() => {
          pending.delete(id)
          const error = new BridgeRequestError("failed", `'${type}' did not answer within ${timeoutMs}ms`)
          logger.error("native.result_timeout", { viewer, request_id: id, message_type: type }, error)
          reject(error)
        }, timeoutMs)

        pending.set(id, { resolve, reject, timer, viewer, resultType: `${type}.result` })
        try {
          if (postToNativeHost(createRequest(type, id, data))) return
          throw new BridgeRequestError("failed", "The native host did not accept the message")
        } catch (error) {
          pending.delete(id)
          clearTimeout(timer)
          logger.error("native.send_failed", { viewer, request_id: id, message_type: type }, error)
          reject(error)
        }
      }),
    on: (type, handler) => {
      const listeners = handlers.get(type) ?? new Set<InboundHandler>()
      listeners.add(handler)
      handlers.set(type, listeners)
      registered.add(type)
      const initial = initialMessages.get(type)
      if (initial) {
        initialMessages.delete(type)
        handler(initial.data ?? {}, initial)
      }
      return () => {
        listeners.delete(handler)
        if (listeners.size === 0) handlers.delete(type)
      }
    },
    dispose: () => {
      disposed = true
      pageMeta = undefined
      options.dispose?.()
      if (available) beginHostedDocument()
      if (available) window.removeEventListener(BRIDGE_EVENT_NAME, receive)
      for (const waiting of pending.values()) {
        clearTimeout(waiting.timer)
        waiting.reject(new BridgeRequestError("cancelled", "Document disposed"))
      }
      pending.clear()
      handlers.clear()
    },
  }
}

const INERT_BRIDGE: NativeBridge = {
  available: false,
  send: () => false,
  notify: () => false,
  request: (type) => Promise.reject(new BridgeRequestError("capability_unavailable", `'${type}' needs the Nelo app`)),
  on: () => () => {},
  dispose: () => {},
}

let shared: NativeBridge | undefined

let consumers = 0

let teardownScheduled = false

const acquireSharedBridge = (queryClient?: QueryClient): NativeBridge => {
  consumers += 1
  if (shared && !shared.available && isEnvelopeHostAvailable()) {
    shared.dispose()
    shared = undefined
  }
  shared ??= createNativeBridge(queryClient ? { ...createHostedSession(queryClient), viewer: () => currentViewer(queryClient) } : undefined)
  return shared
}

const releaseSharedBridge = (): void => {
  consumers -= 1
  if (consumers > 0 || teardownScheduled) return

  teardownScheduled = true
  queueMicrotask(() => {
    teardownScheduled = false
    if (consumers > 0) return

    shared?.dispose()
    shared = undefined
  })
}

export const useNativeBridge = (): NativeBridge => {
  const queryClient = useContext(QueryClientContext)
  const [bridge, setBridge] = useState<NativeBridge>(INERT_BRIDGE)

  useEffect(() => {
    setBridge(acquireSharedBridge(queryClient))
    const connectDeclaredHost = () => {
      if (!isEnvelopeHostAvailable()) return
      releaseSharedBridge()
      setBridge(acquireSharedBridge(queryClient))
    }
    window.addEventListener("nelo:bridge-host-ready", connectDeclaredHost)
    return () => {
      window.removeEventListener("nelo:bridge-host-ready", connectDeclaredHost)
      releaseSharedBridge()
    }
  }, [queryClient])

  return bridge
}
